<template>
  <teleport to="body">
    <div v-if="visible" class="chgpass-wrapper" :style="{ zIndex: 2147483645 }">
      <div class="modal-backdrop" @click="onClose" :style="{ zIndex: 2147483646 }"></div>
      <div class="modal-content chgpass-modal" role="dialog" aria-modal="true" :style="{ zIndex: 2147483647 }">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ $t('cambiar_contrasena') }}<br />
            <span class="user-name">{{ userName }}</span>
          </h5>
          <button type="button" class="btn-close" @click="onClose" aria-label="Cerrar" title="Cerrar">
            <!-- White X icon -->
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
              <line x1="4" y1="4" x2="20" y2="20" stroke="#fff" stroke-width="2" stroke-linecap="round" />
              <line x1="20" y1="4" x2="4" y2="20" stroke="#fff" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="handleChangePassword">
            <div class="mb-3">
              <label for="old_password_hdr" class="form-label">{{ $t('contrasena_actual') }}</label>
              <div class="password-container">
                <input
                  :type="showCurrentPassword ? 'text' : 'password'"
                  id="old_password_hdr"
                  v-model="passwordForm.old_password"
                  class="form-input"
                  required
                  autocomplete="current-password"
                />
                <span class="material-icons password-toggle" @click="toggleShowCurrentPassword" title="Mostrar/ocultar">
                  {{ showCurrentPassword ? 'visibility_off' : 'visibility' }}
                </span>
              </div>
            </div>

            <div class="mb-3">
              <label for="new_password_hdr" class="form-label">{{ $t('nueva_contrasena') }}</label>
              <div class="password-container">
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  id="new_password_hdr"
                  v-model="passwordForm.new_password"
                  class="form-input"
                  required
                  autocomplete="new-password"
                />
                <span class="material-icons password-toggle" @click="toggleShowNewPassword" title="Mostrar/ocultar">
                  {{ showNewPassword ? 'visibility_off' : 'visibility' }}
                </span>
              </div>
            </div>

            <div class="mb-3">
              <label for="new_password_confirmation_hdr" class="form-label">{{ $t('repetir_contraseña') }}</label>
              <div class="password-container">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  id="new_password_confirmation_hdr"
                  v-model="passwordForm.new_password_confirmation"
                  class="form-input"
                  required
                  autocomplete="new-password"
                />
                <span class="material-icons password-toggle" @click="toggleShowConfirmPassword" title="Mostrar/ocultar">
                  {{ showConfirmPassword ? 'visibility_off' : 'visibility' }}
                </span>
              </div>
            </div>

            <div class="modal-footer">
              <button type="submit" class="btn btn-primary" :disabled="isSaving">
                <span v-if="isSaving">{{ $t('guardando') }}…</span>
                <span v-else>{{ $t('guardar') }}</span>
              </button>
              <button type="button" class="btn btn-secondary" @click="onClose">{{ $t('cancelar') }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import api from '@/api.js';
import Swal from 'sweetalert2';
import { useUserStore } from '@/stores/user';

export default {
  name: 'ChangePasswordModalHeader',
  props: {
    visible: { type: Boolean, required: true },
    userName: { type: String, default: '' },
    userId: { type: [String, Number, null], default: null },
  },
  emits: ['close', 'password-changed'],
  data() {
    return {
      passwordForm: {
        old_password: '',
        new_password: '',
        new_password_confirmation: '',
        forzar_cambio_contrasena: true,
      },
      isSaving: false,
      showNewPassword: false,
      showConfirmPassword: false,
      showCurrentPassword: false,
    };
  },
  watch: {
    visible(val) {
      if (typeof document !== 'undefined') {
        document.body.style.overflow = val ? 'hidden' : '';
        document.body.style.position = val ? 'relative' : '';
      }
      if (!val) {
        this.passwordForm.old_password = '';
        this.passwordForm.new_password = '';
        this.passwordForm.new_password_confirmation = '';
        this.isSaving = false;
        this.showNewPassword = false;
        this.showConfirmPassword = false;
        this.showCurrentPassword = false;
      }
    }
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    toggleShowNewPassword() { this.showNewPassword = !this.showNewPassword; },
    toggleShowConfirmPassword() { this.showConfirmPassword = !this.showConfirmPassword; },
    toggleShowCurrentPassword() { this.showCurrentPassword = !this.showCurrentPassword; },
    async handleChangePassword() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        const current = String(this.passwordForm.old_password || '');
        const pwd = String(this.passwordForm.new_password || '');
        const confirm = String(this.passwordForm.new_password_confirmation || '');

        if (pwd.length < 12) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('new_password_min_length') || 'La nueva contraseña debe tener al menos 12 caracteres.', confirmButtonColor: '#99172A' });
          this.isSaving = false; return;
        }

        const pwdRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#\$%\^&\*()_+\-=[\]{}|;:,.<>?]).{12,}$/;
        if (!pwdRegex.test(pwd)) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('contrasenanovalida') || 'La nueva contraseña no cumple los requisitos de complejidad.', confirmButtonColor: '#99172A' });
          this.isSaving = false; return;
        }

        if (pwd !== confirm) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('contrasenasnocoinciden') || 'Las contraseñas no coinciden.', confirmButtonColor: '#99172A' });
          this.isSaving = false; return;
        }

        if (current && current === pwd) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('new_password_different') || 'La nueva contraseña debe ser diferente a la actual.', confirmButtonColor: '#99172A' });
          this.isSaving = false; return;
        }

        await api.post('/users/change-password', {
          current_password: current,
          new_password: pwd,
          new_password_confirmation: confirm
        });

        try {
          const store = useUserStore();
          const uid = this.userId || store.id;
          await api.post('/logs', {
            accion: 'cambio_contrasena',
            usuario_id: uid,
            descripcion: 'Usuario cambió su contraseña',
            fecha: new Date().toISOString()
          });
        } catch (logErr) {
          console.warn('No se pudo registrar el log de cambio de contraseña:', logErr);
        }

        await Swal.fire({ icon: 'success', title: this.$t('contraseña_actualizada'), confirmButtonColor: '#99172A' });
        this.$emit('password-changed');
        this.onClose();
      } catch (err) {
        const errorMessage = err.response?.data?.message || this.$t('error_cambiar_contrasena') || 'Error';
        await Swal.fire({ icon: 'error', title: 'Error', text: errorMessage, confirmButtonColor: '#99172A' });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.chgpass-wrapper { position: fixed; inset: 0; display:flex; align-items:center; justify-content:center; z-index:20000; }
.modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index:20001; }
.chgpass-modal.modal-content { position: fixed; top:50%; left:50%; transform: translate(-50%,-50%); z-index:20002; width:100%; max-width:520px; background:#fff; border-radius:6px; box-shadow:0 8px 30px rgba(0,0,0,0.25); }
.modal-header { position: relative; margin-left: 20px; display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom: none; background: #99172A; color: #fff; border-radius: 6px 6px 0 0; }
.modal-title { margin:0; font-size:16px; font-weight:700; color: #fff; }
.user-name { font-size:12px; color: rgba(255,255,255,0.9); font-weight:500; }
.btn-close { position: absolute; top: 12px; left: 5px; background: rgba(255,255,255,0.18); border:none; cursor:pointer; padding:6px; border-radius:50%; width:36px; height:36px; display:flex; align-items:center; justify-content:center; z-index:21000; }
.btn-close svg { width:18px; height:18px; display:block; }
.btn-close svg line { stroke: #ffffff; stroke-opacity: 1; stroke-width: 2; }
.modal-body { padding:16px; }
.form-label { display:block; margin-bottom:6px; font-weight:600; }
.form-input { width:100%; padding:8px 10px; border:1px solid #ddd; border-radius:4px; }
.password-container { position:relative; }
.password-toggle { position:absolute; right:8px; top:50%; transform:translateY(-50%); cursor:pointer; color:#99172A; }
.modal-footer { display:flex; gap:8px; justify-content:flex-end; padding:12px 16px; border-top:1px solid #eee; }
</style>
